/**
 * Справочники предметной области: стадии дела, источники, статусы входящих,
 * типы ходов диалога, основания продления срока.
 *
 * Стадия — реальная колонка `gms_cases.stage`, её нельзя вычислять из возраста дела
 * (решение от 2026-09-07). Порядок массива `STAGES` задаёт сравнение «не раньше чем».
 */

import type {
    InboxStatus,
    Role,
    Source,
    Stage,
    TurnType,
    Verdict,
} from '../api/types';

export const STAGES: readonly Stage[] = [
    'registered',
    'qualified',
    'investigation',
    'draft',
    'approval',
    'sent',
];

export const STAGE_LABELS: Readonly<Record<Stage, string>> = {
    registered: 'Зарегистрировано',
    qualified: 'Квалифицировано',
    investigation: 'Разбирательство',
    draft: 'Проект ответа',
    approval: 'На согласовании',
    sent: 'Ответ направлен',
};

export function isStage(value: unknown): value is Stage {
    return typeof value === 'string' && (STAGES as readonly string[]).includes(value);
}

export function stageIndex(stage: Stage | string | null | undefined): number {
    if (!stage) return -1;
    return (STAGES as readonly string[]).indexOf(stage);
}

export function stageLabel(stage: Stage | string | null | undefined): string {
    return isStage(stage) ? STAGE_LABELS[stage] : '';
}

/** Класс чипа стадии: `stage-chip s-investigation`. */
export function stageChipClass(stage: Stage | string | null | undefined): string {
    return isStage(stage) ? 'stage-chip s-' + stage : 'stage-chip';
}

export function isOpenStage(stage: Stage | string | null | undefined): boolean {
    return stage !== 'sent';
}

export type StageStepState = 'done' | 'current' | 'todo';

export interface StageStep {
    stage: Stage;
    label: string;
    state: StageStepState;
}

/** Хлебные крошки стадий для шапки карточки дела (`.stage-track`). */
export function stageSteps(current: Stage | string | null | undefined): StageStep[] {
    const idx = stageIndex(current);
    return STAGES.map(
        (stage, i): StageStep => ({
            stage,
            label: STAGE_LABELS[stage],
            state: i < idx ? 'done' : i === idx ? 'current' : 'todo',
        }),
    );
}

export const ROLE_LABELS: Readonly<Record<Role, string>> = {
    chief: 'Главный врач',
    executor: 'Исполнитель',
};

/** Пять оснований продления — строки передаются в `POST /cases/:id/extend` как есть. */
export const EXTEND_REASONS: readonly string[] = [
    'Запрошены объяснительные',
    'Назначена врачебная комиссия',
    'Ожидается ответ смежной организации',
    'Запрошены документы у заявителя',
    'Иное',
];

/** При «Иное» поле `comment` обязательно. */
export const EXTEND_REASON_OTHER = 'Иное';

export const SOURCE_LABELS: Readonly<Record<Source, string>> = {
    gosuslugi: 'Госуслуги',
    email: 'Электронная почта',
    site: 'Сайт клиники',
    bot: 'Бот',
    manual: 'Внесено вручную',
    telegram_mini: 'Telegram (мини)',
    demo: 'Демо-данные',
};

export function sourceLabel(source: Source | string | null | undefined): string {
    if (!source) return '—';
    return (SOURCE_LABELS as Record<string, string>)[source] ?? source;
}

export const INBOX_STATUS_LABELS: Readonly<Record<InboxStatus, string>> = {
    new: 'В очереди',
    processing: 'В разборе',
    done: 'Разобрано',
    failed: 'Ошибка разбора',
    needs_confirm: 'Требует подтверждения',
};

/** Незавершённые статусы: пока входящее в них, фронт опрашивает статус раз в 2 с. */
export const INBOX_PENDING_STATUSES: readonly InboxStatus[] = ['new', 'processing'];

export function isInboxPending(status: InboxStatus | string | null | undefined): boolean {
    return (INBOX_PENDING_STATUSES as readonly string[]).includes(String(status));
}

/** Метка хода диалога для `.turn-badge` (в макете первый разбор помечен «квалификация»). */
export const TURN_TYPE_LABELS: Readonly<Record<TurnType, string>> = {
    appeal: 'обращение',
    initial: 'квалификация',
    question: 'вопрос',
    facts: 'фактура',
    new_appeal: 'новое обращение',
};

export function turnTypeLabel(turn: TurnType | string | null | undefined): string {
    if (!turn) return '';
    return (TURN_TYPE_LABELS as Record<string, string>)[turn] ?? turn;
}

export const VERDICT_LABELS: Readonly<Record<Verdict, string>> = {
    ok: 'Замечаний нет',
    issues: 'Есть замечания',
};

/** Подписи источников пунктов чек-листа (`ChecklistItem.source`). */
export const CHECKLIST_SOURCE_LABELS: Readonly<Record<string, string>> = {
    assignment: 'поручение',
    inquiry: 'запрос',
    clarification: 'уточнить у заявителя',
    document: 'документ',
    agent: 'добавлено ИИ',
};

export function checklistSourceLabel(source: string | null | undefined): string {
    if (!source) return '';
    return CHECKLIST_SOURCE_LABELS[source] ?? source;
}
